// components/FileInput.jsx
import React from 'react'
import {useField} from 'formik'
import clsx from 'clsx'

interface FileInputProps {
    name: string
    label?: string
    required?: boolean
    disabled?: boolean
    accept?: string
    multiple?: boolean
    onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
}

export function FileInput({
    label,
    name,
    required = false,
    disabled = false,
    accept,
    multiple,
    onChange,
    ...props
}: FileInputProps) {
    const [field, meta, helpers] = useField(name)

    // meta.value agora contém o File object (ou null/undefined se nenhum arquivo)
    const selectedFile = meta.value

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.currentTarget.files
        if (multiple) {
            helpers.setValue(files ? Array.from(files) : [])
        } else {
            helpers.setValue(files && files.length > 0 ? files[0] : null)
        }
    }

    const handleClearFile = () => {
        helpers.setValue(multiple ? [] : null)
        const inputElement = document.getElementById(name) as HTMLInputElement
        if (inputElement) {
            inputElement.value = ''
        }
    }

    return (
        <>
            {label && (
                <label htmlFor={name} className='form-label fs-6 fw-bolder'>
                    {label}
                    {required && <span className='text-danger'>*</span>}
                </label>
            )}
            <div className='input-group flex-nowrap flex-column'>
                <div className='input-group'>
                    <input
                        id={name}
                        name={name}
                        type='file'
                        className={clsx(
                            'form-control form-control-lg form-control-solid',
                            {
                                'is-invalid': meta.touched && meta.error,
                            },
                            {
                                'is-valid': meta.touched && !meta.error,
                            }
                        )}
                        onChange={(e) => {
                            if (onChange !== undefined) {
                                onChange(e)
                            }
                            handleFileChange(e)
                        }}
                        onBlur={field.onBlur}
                        disabled={disabled}
                        accept={accept}
                        multiple={multiple}
                        {...props}
                    />
                </div>

                {selectedFile && !multiple && (
                    <div className='input-group-text d-flex align-items-center text-wrap'>
                        <span className='me-2 w-100'>
                            {selectedFile.name} ({Math.round(selectedFile.size / 1024)} KB)
                        </span>
                        <button
                            type='button'
                            className='btn btn-sm btn-light-danger'
                            onClick={handleClearFile}
                            title='Remover arquivo'
                        >
                            ✖
                        </button>
                    </div>
                )}

                {selectedFile && multiple && selectedFile.length > 0 && (
                    <div className='input-group-text d-flex align-items-center flex-wrap'>
                        <span
                            className='me-2'
                            style={{
                                textOverflow: 'ellipsis',
                                maxWidth: '100%',
                                overflowWrap: 'normal',
                            }}
                        >
                            {selectedFile.length} arquivo(s) selecionado(s):
                            <ul>
                                {selectedFile.map((file: File, index: number) => (
                                    <li key={file.name + index}>
                                        {file.name} ({Math.round(file.size / 1024)} KB)
                                    </li>
                                ))}
                            </ul>
                        </span>
                        <button
                            type='button'
                            className='btn btn-sm btn-light-danger btn-icon'
                            onClick={handleClearFile}
                            title='Remover todos os arquivos'
                        >
                            ✖
                        </button>
                    </div>
                )}

                {meta.touched && meta.error && (
                    <div className='fv-plugins-message-container'>
                        <div className='fv-help-block'>
                            <span role='alert' className='text-danger'>
                                {meta.error}
                            </span>
                        </div>
                    </div>
                )}
            </div>
        </>
    )
}
